import type { DocRef } from '@lms/shared-types'
import { Icon } from '../primitives/Icon'

/**
 * One attached document on the leave-detail page: filename, size, preview.
 *
 * Promoted verbatim from `DocumentVerifiedSummary`'s `!flags.aiEvidenceOcr`
 * fallback branch, which was the ONLY branch that ever rendered in a deployed
 * environment — the flag was false in staging and production. The verdict
 * chrome it fell back from (needs-review / dates-don't-match / no-letterhead)
 * went with the OCR surface in SG#7.
 *
 * Deliberately not re-styled while extracting: this is the markup that ships
 * today, so lifting it unchanged means the leave-detail attachment list looks
 * exactly as it did before the removal.
 */
export function DocumentAttachmentRow({
  doc,
  onPreview,
}: {
  doc: DocRef
  onPreview: () => void
}) {
  return (
    <button
      type="button"
      data-testid={`document-plain-${doc.id}`}
      onClick={onPreview}
      aria-label={`Preview ${doc.filename}`}
      title={`Preview ${doc.filename}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 8px',
        background: 'transparent',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
      }}
    >
      <Icon
        name="description"
        size={17}
        aria-hidden="true"
        style={{ color: 'var(--steel-blue-3)', flex: 'none' }}
      />
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 500,
          color: 'var(--steel-blue-3)',
          textDecoration: 'underline',
          textUnderlineOffset: 2,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {doc.filename}
      </span>
      <span style={{ fontSize: 11, color: 'var(--text-3)', flex: 'none' }}>
        {formatBytes(doc.byteSize)}
      </span>
      <Icon
        name="open_in_new"
        size={13}
        aria-hidden="true"
        style={{ color: 'var(--text-3)', flex: 'none', marginLeft: 'auto' }}
      />
    </button>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
